"use client";

import { format } from "date-fns";
import type React from "react";
import { useCallback, useState } from "react";
import { Icon } from "./icon";
import { cn } from "../../lib/utils";
import { omitUndefined } from "../../lib/props";
import { Button, type ButtonShape, type ButtonSize, type ButtonVariant } from "./button";
import { Calendar, useCalendarDefaults, type HourCycle } from "./calendar";
import { Popover, PopoverPopup, PopoverTrigger } from "./popover";
import { TimeField } from "./time-field";
import type { Omittable } from "../../lib/props";

export type DatePickerMode = "date" | "datetime" | "time";

/** date-fns pattern for the trigger label, per mode and clock. */
const TRIGGER_FORMAT: Record<HourCycle, Record<DatePickerMode, string>> = {
  12: {
    date: "MMM d, yyyy",
    datetime: "MMM d, yyyy h:mm a",
    time: "h:mm a",
  },
  24: {
    date: "MMM d, yyyy",
    datetime: "MMM d, yyyy HH:mm",
    time: "HH:mm",
  },
};

const MODE_PLACEHOLDER: Record<DatePickerMode, string> = {
  date: "Pick a date",
  datetime: "Pick a date & time",
  time: "Pick a time",
};

export type DatePickerProps = Omittable<{
  /** `date` is a calendar, `time` a typed clock field, `datetime` both. */
  mode?: DatePickerMode;
  /** The selected moment, or null when empty. In `time` mode only the
   * hours/minutes are meaningful — the date rides along at the epoch day. */
  value: Date | null;
  onValueChange: (date: Date | null) => void;
  placeholder?: string;
  /** Trigger scale — the Button scale, forwarded as-is. */
  size?: ButtonSize;
  /** Trigger face — the Button face, forwarded as-is. `outline` (the
   * default) reads as a form field; `ghost` is chromeless, for inline rows
   * that carry their own borders. */
  variant?: ButtonVariant;
  /** Trigger corners — the Button shape, forwarded as-is. Omit for the
   * stadium default; `square` when the picker stands in a form beside
   * rectangular inputs, so the row reads as one field column. */
  shape?: ButtonShape;
  disabled?: boolean;
  /** Matcher for days that cannot be picked. */
  disabledDates?: (date: Date) => boolean;
  align?: React.ComponentProps<typeof PopoverPopup>["align"];
  /** date-fns pattern for the trigger label. Defaults per mode and clock. */
  dateFormat?: string;
  /** The clock the label and the time field read in. Defaults to the
   *  ambient `CalendarDefaultsProvider` clock, else 12. */
  hourCycle?: HourCycle;
  /** For an external `<FieldLabel htmlFor>`. */
  id?: string;
  ariaLabel?: string;
  /** Controlled open state. Omit and the picker owns it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Custom trigger chrome — a `PopoverTrigger` subtree, so the picker can
   * hang off a host's own control (an inline cell, a table cell) instead of
   * the default Button. Omit for the default trigger. */
  children?: React.ReactNode;
}>;

/**
 * Date / time picker: a `Popover` whose popup is a `Calendar`, a `TimeField`,
 * or both. It never falls through to the browser's native picker.
 *
 * `date` closes on select. `datetime` does not — the day is only half the
 * value, so the popup stays up for the time. There is deliberately no range
 * mode: a range is two of these.
 */
export function DatePicker({
  align = "start",
  ariaLabel,
  children,
  dateFormat,
  disabled,
  disabledDates,
  hourCycle: hourCycleProp,
  id,
  mode = "date",
  onOpenChange,
  onValueChange,
  open: openProp,
  placeholder,
  shape,
  size = "default",
  value,
  variant = "outline",
}: DatePickerProps): React.ReactElement {
  const defaults = useCalendarDefaults();
  const hourCycle: HourCycle = hourCycleProp ?? defaults.hourCycle ?? 12;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const handleSelectDay = useCallback(
    (day: Date | undefined) => {
      if (!day) {
        onValueChange(null);
        return;
      }
      const next = new Date(day);
      if (mode === "datetime") {
        // Keep the time already on the value; a first pick starts at midnight.
        next.setHours(value?.getHours() ?? 0, value?.getMinutes() ?? 0, 0, 0);
        onValueChange(next);
        // Deliberately stays open — the time half is still unset.
        return;
      }
      onValueChange(next);
      setOpen(false);
    },
    [mode, onValueChange, setOpen, value],
  );

  const handleTimeChange = useCallback(
    ({ hours, minutes }: { hours: number; minutes: number }) => {
      // A time with no date yet anchors to the epoch day in `time` mode (the
      // conventional time-only carrier) and to today in `datetime` mode.
      const base =
        value ?? (mode === "time" ? new Date(1970, 0, 1) : new Date());
      const next = new Date(base);
      next.setHours(hours, minutes, 0, 0);
      onValueChange(next);
    },
    [mode, onValueChange, value],
  );

  const label = value
    ? format(value, dateFormat ?? TRIGGER_FORMAT[hourCycle][mode])
    : (placeholder ?? MODE_PLACEHOLDER[mode]);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      {children ?? (
        <PopoverTrigger
          aria-label={ariaLabel}
          id={id}
          render={
            <Button
              className="w-full justify-start font-normal"
              disabled={disabled}
              shape={shape}
              size={size}
              variant={variant}
            />
          }
        >
          {mode === "time" ? (
            <Icon name="clock" />
          ) : (
            <Icon name="calendar" />
          )}
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-left",
              !value && "text-muted-foreground/72",
            )}
          >
            {label}
          </span>
        </PopoverTrigger>
      )}
      <PopoverPopup align={align}>
        <div className="flex flex-col gap-2">
          {mode !== "time" && (
            <Calendar
              mode="single"
              onSelect={handleSelectDay}
              {...omitUndefined({
                defaultMonth: value ?? undefined,
                disabled: disabledDates,
                selected: value ?? undefined,
              })}
            />
          )}
          {mode !== "date" && (
            // No clock glyph here: the trigger already wears it, and a
            // second one in the popup read as two controls.
            <TimeField
              ariaLabel="Time"
              hourCycle={hourCycle}
              onValueChange={handleTimeChange}
              value={
                value ? { hours: value.getHours(), minutes: value.getMinutes() } : null
              }
            />
          )}
        </div>
      </PopoverPopup>
    </Popover>
  );
}
